/**
 * ChatTransferBundleCipher.ts
 *
 * AES-GCM-256 chunked seal / open of the chat bundle bytes under the
 * `transferKey`.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ChatTransferBundleCipher.swift` but
 * with a streaming, chunked layout that lets the WebRTC datachannel ship a
 * 100MB bundle without exceeding AES-GCM's per-nonce 64GB safety bound (and,
 * less relevantly, without holding the whole ciphertext in memory).
 *
 * ## Chunk frame layout
 *
 * The output of `sealBundle` is a flat `Uint8Array` containing one
 * length-prefixed chunk per call. Each chunk is:
 *
 *     [ u32 BE: ciphertext + tag length ][ ciphertext + 16-byte tag ]
 *
 * Each chunk's nonce is **derived deterministically** from a 12-byte base
 * nonce + a 32-bit counter so the peer recomputes the same nonce sequence
 * and can verify per-chunk auth tags as the stream arrives. Per-chunk
 * derived nonce keeps the encryption side stateless across re-seals — the
 * caller never has to remember "which nonce did I last use?"
 *
 * Nonce construction:
 *
 *     baseNonce[12 bytes] = first 12 bytes of HKDF-SHA256(transferKey,
 *                                                         salt=0×32,
 *                                                         info="kaether:chat-transfer:nonce:v1",
 *                                                         len=12)
 *     chunkNonce[i] = baseNonce XOR (BE32(i) padded-left with zeros)
 *
 * The XOR happens over the trailing 4 bytes (the "counter slot"). A 32-bit
 * counter at 1MB-per-chunk supports 4PB of throughput — orders of magnitude
 * beyond the 100MB cap. The risk of nonce collision is therefore reduced to
 * "the same transferKey is used to seal two different bundles in two
 * different sessions" — which the session-id-in-AAD guard handles.
 *
 * ## AAD per chunk
 *
 * Every chunk's AAD is:
 *
 *     "kaether:chat-transfer:bundle:v1:" ‖ transferId ‖ BE32(chunkIndex)
 *
 * The chunk index is in AAD (not just nonce) so a reorder attack — peer
 * sends chunk N then chunk N-1 — fails at the AEAD layer rather than the
 * application layer. Each chunk is bound to exactly its position.
 *
 * ## 100MB cap
 *
 * `MAX_BUNDLE_BYTES = 100 * 1024 * 1024`. `sealBundle` refuses oversize
 * plaintext outright. `openBundleStream` enforces the same cap on the sum
 * of decoded plaintexts so a hostile peer cannot stream more than the cap
 * worth of bytes into the receiver.
 */

import { TRANSFER_KEY_BYTES } from "./ChatTransferKeyAgreement";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Default plaintext chunk size — 1MB. Tunable; keep below GCM's 64GB/nonce
 *  budget by a wide margin. */
export const DEFAULT_CHUNK_BYTES = 1 * 1024 * 1024;

/** Hard cap on the bundle. Refused at seal-time and again at open-time. */
export const MAX_BUNDLE_BYTES = 100 * 1024 * 1024;

const AAD_PREFIX = new TextEncoder().encode("kaether:chat-transfer:bundle:v1:");
const NONCE_INFO = new TextEncoder().encode("kaether:chat-transfer:nonce:v1");
const HKDF_SALT_ZERO_32 = new Uint8Array(32);

const NONCE_BYTES = 12;
const GCM_TAG_BYTES = 16;
const LENGTH_PREFIX_BYTES = 4;

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type ChatTransferCipherErrorKind =
  | "bundleOverLimit"
  | "openFailure"
  | "sealFailure"
  | "frameTruncated"
  | "frameLengthMismatch"
  | "keyWrongLength";

export class ChatTransferCipherError extends Error {
  readonly kind: ChatTransferCipherErrorKind;
  readonly observedBytes?: number;
  constructor(
    kind: ChatTransferCipherErrorKind,
    message: string,
    extra?: { observedBytes?: number },
  ) {
    super(message);
    this.name = "ChatTransferCipherError";
    this.kind = kind;
    this.observedBytes = extra?.observedBytes;
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function writeBigEndianU32(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset + 0] = (value >>> 24) & 0xff;
  bytes[offset + 1] = (value >>> 16) & 0xff;
  bytes[offset + 2] = (value >>> 8) & 0xff;
  bytes[offset + 3] = value & 0xff;
}

function readBigEndianU32(bytes: Uint8Array, offset: number): number {
  return (
    bytes[offset + 0] * 0x100_0000 +
    (bytes[offset + 1] << 16) +
    (bytes[offset + 2] << 8) +
    bytes[offset + 3]
  );
}

async function importKey(transferKey: Uint8Array): Promise<CryptoKey> {
  if (transferKey.length !== TRANSFER_KEY_BYTES) {
    throw new ChatTransferCipherError(
      "keyWrongLength",
      `transferKey must be ${TRANSFER_KEY_BYTES} bytes`,
    );
  }
  return crypto.subtle.importKey(
    "raw",
    transferKey,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
}

async function deriveBaseNonce(transferKey: Uint8Array): Promise<Uint8Array> {
  const ikmKey = await crypto.subtle.importKey(
    "raw",
    transferKey,
    { name: "HKDF" },
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: HKDF_SALT_ZERO_32, info: NONCE_INFO },
    ikmKey,
    NONCE_BYTES * 8,
  );
  return new Uint8Array(bits);
}

function chunkNonce(baseNonce: Uint8Array, chunkIndex: number): Uint8Array {
  // XOR a big-endian uint32 counter into the trailing 4 bytes of the base
  // nonce. The 8 leading bytes are pure HKDF output; the trailing 4 bytes
  // act as the counter slot.
  const nonce = new Uint8Array(NONCE_BYTES);
  nonce.set(baseNonce, 0);
  const ctrBytes = new Uint8Array(4);
  writeBigEndianU32(ctrBytes, 0, chunkIndex);
  for (let i = 0; i < 4; i++) {
    nonce[NONCE_BYTES - 4 + i] ^= ctrBytes[i];
  }
  return nonce;
}

function buildAad(transferId: string, chunkIndex: number): Uint8Array {
  const idBytes = new TextEncoder().encode(transferId);
  const aad = new Uint8Array(AAD_PREFIX.length + idBytes.length + 4);
  aad.set(AAD_PREFIX, 0);
  aad.set(idBytes, AAD_PREFIX.length);
  writeBigEndianU32(aad, AAD_PREFIX.length + idBytes.length, chunkIndex);
  return aad;
}

// ---------------------------------------------------------------------------
// Public API — chunk-level (exposed for tampering tests + future streaming)
// ---------------------------------------------------------------------------

export interface SealedBundle {
  readonly chunks: readonly Uint8Array[];
  readonly chunkCount: number;
}

/**
 * Seal an entire bundle into a sequence of independently authenticated
 * chunks, returned in order.
 *
 * Caller chunk-by-chunk streams `chunks[i]` onto the wire. Each entry is
 * already length-prefixed; concatenation yields the canonical stream
 * format that `openBundleStream` consumes.
 */
export async function sealBundle(
  plaintext: Uint8Array,
  transferKey: Uint8Array,
  transferId: string,
  chunkSize: number = DEFAULT_CHUNK_BYTES,
): Promise<SealedBundle> {
  if (plaintext.length > MAX_BUNDLE_BYTES) {
    throw new ChatTransferCipherError(
      "bundleOverLimit",
      `bundle is ${plaintext.length} bytes; cap is ${MAX_BUNDLE_BYTES}`,
      { observedBytes: plaintext.length },
    );
  }
  if (chunkSize <= 0 || !Number.isFinite(chunkSize)) {
    throw new ChatTransferCipherError(
      "sealFailure",
      "chunkSize must be a positive integer",
    );
  }

  const key = await importKey(transferKey);
  const baseNonce = await deriveBaseNonce(transferKey);

  const chunks: Uint8Array[] = [];
  // Handle empty plaintext as a single zero-byte chunk so the framing
  // self-describes "this is a complete bundle, no data" rather than "this
  // is a truncated zero-chunk stream".
  const totalChunks =
    plaintext.length === 0
      ? 1
      : Math.ceil(plaintext.length / chunkSize);

  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, plaintext.length);
    const chunkPlaintext =
      plaintext.length === 0 ? new Uint8Array(0) : plaintext.subarray(start, end);

    const nonce = chunkNonce(baseNonce, i);
    const aad = buildAad(transferId, i);

    let ciphertextBuf: ArrayBuffer;
    try {
      ciphertextBuf = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv: nonce, additionalData: aad },
        key,
        chunkPlaintext,
      );
    } catch (err) {
      throw new ChatTransferCipherError(
        "sealFailure",
        `AES-GCM seal failed at chunk ${i}: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
    const ciphertext = new Uint8Array(ciphertextBuf);
    const framed = new Uint8Array(LENGTH_PREFIX_BYTES + ciphertext.length);
    writeBigEndianU32(framed, 0, ciphertext.length);
    framed.set(ciphertext, LENGTH_PREFIX_BYTES);
    chunks.push(framed);
  }

  return { chunks, chunkCount: totalChunks };
}

/**
 * Streaming open. Takes the concatenated framed-chunk stream produced by
 * `sealBundle` and returns the recovered plaintext.
 *
 * Per-chunk auth-tag failure rejects with `openFailure` and aborts the
 * whole open — there is no partial-acceptance mode. Cumulative plaintext is
 * checked against `MAX_BUNDLE_BYTES` as chunks decrypt; a hostile peer
 * sending an oversize stream is rejected before all of memory is consumed.
 */
export async function openBundleStream(
  stream: Uint8Array,
  transferKey: Uint8Array,
  transferId: string,
): Promise<Uint8Array> {
  const key = await importKey(transferKey);
  const baseNonce = await deriveBaseNonce(transferKey);

  const decoded: Uint8Array[] = [];
  let cursor = 0;
  let chunkIndex = 0;
  let cumulativePlain = 0;

  while (cursor < stream.length) {
    if (cursor + LENGTH_PREFIX_BYTES > stream.length) {
      throw new ChatTransferCipherError(
        "frameTruncated",
        `truncated length prefix at offset ${cursor}`,
      );
    }
    const ctLen = readBigEndianU32(stream, cursor);
    cursor += LENGTH_PREFIX_BYTES;
    if (ctLen < GCM_TAG_BYTES) {
      throw new ChatTransferCipherError(
        "frameLengthMismatch",
        `chunk ${chunkIndex} ciphertext too short to contain GCM tag`,
      );
    }
    if (cursor + ctLen > stream.length) {
      throw new ChatTransferCipherError(
        "frameTruncated",
        `chunk ${chunkIndex} truncated: header claims ${ctLen} bytes but only ${stream.length - cursor} remain`,
      );
    }
    const ciphertext = stream.subarray(cursor, cursor + ctLen);
    cursor += ctLen;

    const nonce = chunkNonce(baseNonce, chunkIndex);
    const aad = buildAad(transferId, chunkIndex);
    let plainBuf: ArrayBuffer;
    try {
      plainBuf = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: nonce, additionalData: aad },
        key,
        ciphertext,
      );
    } catch {
      throw new ChatTransferCipherError(
        "openFailure",
        `AES-GCM open failed at chunk ${chunkIndex}`,
      );
    }
    const plain = new Uint8Array(plainBuf);
    cumulativePlain += plain.length;
    if (cumulativePlain > MAX_BUNDLE_BYTES) {
      throw new ChatTransferCipherError(
        "bundleOverLimit",
        `cumulative plaintext exceeded ${MAX_BUNDLE_BYTES} bytes at chunk ${chunkIndex}`,
        { observedBytes: cumulativePlain },
      );
    }
    decoded.push(plain);
    chunkIndex += 1;
  }

  // Concatenate the decoded chunks. `Uint8Array.set` against a single
  // allocation is faster than repeatedly building intermediate views.
  const total = new Uint8Array(cumulativePlain);
  let off = 0;
  for (const piece of decoded) {
    total.set(piece, off);
    off += piece.length;
  }
  return total;
}

/**
 * Helper: concatenate the `chunks` returned from `sealBundle` into the
 * single buffer that `openBundleStream` consumes. Useful for unit tests and
 * for callers that do not need true streaming.
 */
export function concatenateChunks(chunks: readonly Uint8Array[]): Uint8Array {
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}
